import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Modal, 
  Select, 
  message, 
  Popconfirm, 
  Row, 
  Col, 
  DatePicker,
  Spin,
  Alert,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  FileTextOutlined, 
  CalendarOutlined,
  CopyOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { usePatientChart } from '../context/PatientChartContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const WeeklyIdtPatientList = () => {
  const { addChart, showChart, triggerChartAction } = usePatientChart();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weeklyPatients, setWeeklyPatients] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(moment().startOf('week'));
  const [isAddPatientModalVisible, setIsAddPatientModalVisible] = useState(false);
  const [isCopyWeekModalVisible, setIsCopyWeekModalVisible] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Get current week range for display
  const getWeekRange = (weekStart) => {
    const start = moment(weekStart).startOf('week');
    const end = moment(weekStart).endOf('week');
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  // Fetch weekly patient list
  const fetchWeeklyPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const weekStart = currentWeek.format('YYYY-MM-DD');
      const response = await axios.get(`/weekly-idt-patients?weekStart=${weekStart}`);
      setWeeklyPatients(response.data.data.patients || []);
    } catch (err) {
      console.error('Error fetching weekly IDT patients:', err);
      setError('Failed to load weekly IDT list.');
    } finally {
      setLoading(false);
    }
  }, [currentWeek]);

  // Fetch all patients for selection
  const fetchAllPatients = useCallback(async (searchTerm = '') => {
    setSearchLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm, limit: 50 } : { limit: 100 };
      const response = await axios.get('/patients', { params });
      const patients = response.data.data.patients || [];
      setAllPatients(patients.map(p => ({
        value: p._id,
        label: `${p.lastName}${p.middleName ? ' ' + p.middleName : ''}, ${p.firstName} (MRN: ${p.mrn || 'N/A'})`,
        patient: p
      })));
    } catch (err) {
      console.error('Error fetching patients:', err);
      message.error('Failed to load patients for selection.');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Load data on component mount and week change
  useEffect(() => {
    fetchWeeklyPatients();
  }, [fetchWeeklyPatients]);

  // Load all patients when add modal opens
  useEffect(() => {
    if (isAddPatientModalVisible) {
      fetchAllPatients();
    }
  }, [isAddPatientModalVisible, fetchAllPatients]);

  // Week navigation
  const navigateWeek = (direction) => {
    const newWeek = moment(currentWeek).add(direction, 'week');
    setCurrentWeek(newWeek);
  };

  // Add patients to weekly list
  const handleAddPatients = async () => {
    if (selectedPatientIds.length === 0) {
      message.warning('Please select at least one patient.');
      return;
    }

    try {
      const weekStart = currentWeek.format('YYYY-MM-DD');
      await axios.post('/weekly-idt-patients', {
        weekStart,
        patientIds: selectedPatientIds
      });
      
      message.success(`Added ${selectedPatientIds.length} patient(s) to weekly IDT list.`);
      setSelectedPatientIds([]);
      setIsAddPatientModalVisible(false);
      fetchWeeklyPatients();
    } catch (err) {
      console.error('Error adding patients to weekly list:', err);
      message.error('Failed to add patients to weekly list.');
    }
  };

  // Remove patient from weekly list
  const handleRemovePatient = async (patientId) => {
    try {
      const weekStart = currentWeek.format('YYYY-MM-DD');
      await axios.delete(`/weekly-idt-patients/${patientId}?weekStart=${weekStart}`);
      
      message.success('Patient removed from weekly IDT list.');
      fetchWeeklyPatients();
    } catch (err) {
      console.error('Error removing patient from weekly list:', err);
      message.error('Failed to remove patient from weekly list.');
    }
  };

  // Handle IDT Note view/create with automatic management
  const handleIdtNoteAction = async (patient) => {
    try {
      const weekStart = currentWeek.format('YYYY-MM-DD');
      const patientName = `${patient.lastName},${patient.middleName ? ` ${patient.middleName}` : ''} ${patient.firstName}`;

      console.log('🔍 WeeklyIdtPatientList: Getting or creating IDT Note for patient:', {
        patientId: patient._id,
        weekStart: weekStart,
        patientName: patientName
      });

      // Call the new automatic IDT note management endpoint
      const response = await axios.get(`/patients/${patient._id}/idt-note-auto`, {
        params: {
          weekStart: weekStart
        }
      });

      const { encounter, action } = response.data.data;
      console.log('✅ WeeklyIdtPatientList: IDT Note management result:', { action, encounterId: encounter._id });

      // Open patient chart
      addChart(patient);
      showChart();

      // Trigger appropriate chart action based on the encounter state and action
      if (action === 'found_existing') {
        if (encounter.isSigned) {
          // If signed, open for viewing
          console.log('📖 WeeklyIdtPatientList: Triggering VIEW_IDT_NOTE action for signed encounter');
          triggerChartAction({
            type: 'VIEW_IDT_NOTE',
            patientId: patient._id,
            encounter: encounter
          });
          message.success(`Opening existing IDT Note for ${patientName} (Read-only - Signed).`);
        } else {
          // If not signed, open for editing
          console.log('✏️ WeeklyIdtPatientList: Triggering EDIT_IDT_NOTE action for unsigned encounter');
          triggerChartAction({
            type: 'EDIT_IDT_NOTE',
            patientId: patient._id,
            encounter: encounter
          });
          message.success(`Opening existing IDT Note for ${patientName} (Editable - Draft).`);
        }
      } else if (action === 'created_new') {
        // New IDT Note was created, open for editing
        console.log('➕ WeeklyIdtPatientList: Triggering EDIT_IDT_NOTE action for newly created encounter');
        triggerChartAction({
          type: 'EDIT_IDT_NOTE',
          patientId: patient._id,
          encounter: encounter
        });
        message.success(`Created new IDT Note for ${patientName}. Opening for editing.`);
      }

    } catch (err) {
      console.error('❌ WeeklyIdtPatientList: Error with automatic IDT Note management:', err);
      const patientName = `${patient.lastName},${patient.middleName ? ` ${patient.middleName}` : ''} ${patient.firstName}`;

      // Show appropriate error message
      if (err.response?.status === 400) {
        message.error(`Invalid request: ${err.response.data.message}`);
      } else if (err.response?.status === 404) {
        message.error(`Patient not found: ${patientName}`);
      } else {
        message.error(`Failed to manage IDT Note for ${patientName}. Please try again.`);
      }

      // Still open the chart as fallback
      addChart(patient);
      showChart();
    }
  };

  // Copy patients from previous week
  const handleCopyFromPreviousWeek = async () => {
    try {
      const previousWeekStart = currentWeek.clone().subtract(1, 'week').format('YYYY-MM-DD');
      const currentWeekStart = currentWeek.format('YYYY-MM-DD');
      
      const response = await axios.post('/weekly-idt-patients/copy', {
        fromWeekStart: previousWeekStart,
        toWeekStart: currentWeekStart
      });
      
      const copiedCount = response.data.data.copiedCount || 0;
      message.success(`Copied ${copiedCount} patient(s) from previous week.`);
      setIsCopyWeekModalVisible(false);
      fetchWeeklyPatients();
    } catch (err) {
      console.error('Error copying from previous week:', err);
      message.error('Failed to copy patients from previous week.');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => {
        // Vietnamese naming convention: Last Name, Middle Name, First Name
        const middleName = record.middleName ? ` ${record.middleName}` : '';
        const fullName = `${record.lastName},${middleName} ${record.firstName}`;

        const handleNameClick = () => {
          addChart(record);
          showChart();
        };

        return (
          <Space direction="vertical" size="small">
            <Text
              strong
              style={{
                color: '#1890ff',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={handleNameClick}
              title="Click to open patient chart"
            >
              {fullName}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'MRN',
      dataIndex: 'mrn',
      key: 'mrn',
      render: (mrn) => mrn || 'N/A',
    },
    {
      title: 'Date of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      render: (date) => date ? moment(date).format('MM/DD/YYYY') : 'N/A',
    },
    {
      title: 'Care 360 Start Date',
      dataIndex: 'care360StartDate',
      key: 'care360StartDate',
      render: (date) => date ? moment(date).format('MM/DD/YYYY') : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => handleIdtNoteAction(record)}
            size="small"
          >
            View/Create IDT Note
          </Button>
          <Popconfirm
            title="Remove patient from weekly IDT list?"
            description="This will remove the patient from this week's IDT review list."
            onConfirm={() => handleRemovePatient(record._id)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space direction="vertical" size="small">
              <Title level={2} style={{ margin: 0 }}>
                <UserOutlined /> Weekly IDT List
              </Title>
              <Text type="secondary">
                Manage patients scheduled for weekly IDT review
              </Text>
            </Space>
          </Col>
        </Row>

        {/* Week Navigation */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => navigateWeek(-1)}
                >
                  Previous Week
                </Button>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {getWeekRange(currentWeek)}
                </Tag>
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => navigateWeek(1)}
                >
                  Next Week
                </Button>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => setIsCopyWeekModalVisible(true)}
                >
                  Copy from Previous Week
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAddPatientModalVisible(true)}
                >
                  Add Patients
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Patient List Table */}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={weeklyPatients}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} patients`
            }}
            locale={{
              emptyText: 'No patients added to this week\'s IDT list yet.'
            }}
          />
        </Spin>
      </Card>

      {/* Add Patients Modal */}
      <Modal
        title="Add Patients to Weekly IDT List"
        open={isAddPatientModalVisible}
        onCancel={() => {
          setIsAddPatientModalVisible(false);
          setSelectedPatientIds([]);
        }}
        onOk={handleAddPatients}
        okText="Add Selected Patients"
        cancelText="Cancel"
        width={800}
        okButtonProps={{ disabled: selectedPatientIds.length === 0 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Select patients to add to the weekly IDT list for {getWeekRange(currentWeek)}:</Text>
        </div>

        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Search and select patients..."
          value={selectedPatientIds}
          onChange={setSelectedPatientIds}
          onSearch={fetchAllPatients}
          loading={searchLoading}
          showSearch
          filterOption={false}
          notFoundContent={searchLoading ? <Spin size="small" /> : 'No patients found'}
        >
          {allPatients
            .filter(p => !weeklyPatients.some(wp => wp._id === p.value))
            .map(patient => (
              <Option key={patient.value} value={patient.value}>
                {patient.label}
              </Option>
            ))}
        </Select>

        {selectedPatientIds.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">
              {selectedPatientIds.length} patient(s) selected
            </Text>
          </div>
        )}
      </Modal>

      {/* Copy from Previous Week Modal */}
      <Modal
        title="Copy Patients from Previous Week"
        open={isCopyWeekModalVisible}
        onCancel={() => setIsCopyWeekModalVisible(false)}
        onOk={handleCopyFromPreviousWeek}
        okText="Copy Patients"
        cancelText="Cancel"
      >
        <div>
          <Text>
            This will copy all patients from the previous week ({getWeekRange(currentWeek.clone().subtract(1, 'week'))})
            to the current week ({getWeekRange(currentWeek)}).
          </Text>
          <br /><br />
          <Text type="secondary">
            Patients already in the current week's list will not be duplicated.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default WeeklyIdtPatientList;
